
import React from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';
import { City } from '../../../data/types/city-types';
import CityMarkers from './CityMarkers';
import ChurchMarkers from './ChurchMarkers';
import { getLowOrUnreachedCities } from '../../../utils/city-utils';
import MapMarkersGeneric from '../MapMarkersGeneric';

interface CityMarkersLayerProps {
  map: mapboxgl.Map;
  selectedRegion: Region | null;
  selectedCity: City | null;
  citiesToShow: City[];
  onCityClick?: (city: City) => void;
  markerRefs: {
    cities: mapboxgl.Marker[];
    churches: mapboxgl.Marker[];
  };
  mapZoomLevel: 'country' | 'region' | 'city';
  showChiapasCities: boolean;
  showHidalgoCities: boolean;
  showGlobalRegions: boolean;
  isMexicoView: boolean;
  filteredRegionIds?: string[];
  showFunding: boolean;
}

const CityMarkersLayer: React.FC<CityMarkersLayerProps> = ({
  map,
  selectedRegion,
  selectedCity,
  citiesToShow,
  onCityClick,
  markerRefs,
  mapZoomLevel,
  showChiapasCities,
  showHidalgoCities,
  showGlobalRegions,
  isMexicoView,
  filteredRegionIds,
  showFunding
}) => {
  // Determine when to show cities based on region and zoom level
  const shouldShowRegionCities = selectedRegion && (mapZoomLevel === 'region' || mapZoomLevel === 'city');
  const shouldShowSpecialCities = selectedRegion && (
    (selectedRegion.region_id === 'CHIS' && (showChiapasCities || mapZoomLevel === 'region' || mapZoomLevel === 'city')) || 
    (selectedRegion.region_id === 'HGO' && (showHidalgoCities || mapZoomLevel === 'region' || mapZoomLevel === 'city'))
  );
  
  // Add data attribute to map container to help with CSS targeting
  React.useEffect(() => {
    if (map) {
      const container = map.getContainer();
      
      if (selectedRegion) {
        container.classList.add('region-view');
        container.setAttribute('data-region', selectedRegion.region_id);
        container.setAttribute('data-zoom-level', mapZoomLevel);
      } else {
        container.classList.remove('region-view');
        container.removeAttribute('data-region');
        container.setAttribute('data-zoom-level', mapZoomLevel);
      }
      
      if (isMexicoView) {
        container.classList.add('mexico-view');
      } else {
        container.classList.remove('mexico-view');
      }
      
      // Add showFunding data attribute
      container.setAttribute('data-show-funding', showFunding ? 'true' : 'false');
      
      return () => {
        if (map) {
          const container = map.getContainer();
          container.classList.remove('region-view', 'mexico-view');
          container.removeAttribute('data-region');
          container.removeAttribute('data-zoom-level');
          container.removeAttribute('data-show-funding');
        }
      };
    }
  }, [map, selectedRegion, isMexicoView, mapZoomLevel, showFunding]);
  
  return (
    <>
      {/* Show generic markers for cities in global or Mexico view */}
      {((showGlobalRegions || isMexicoView) && !selectedRegion) && (
        <MapMarkersGeneric 
          map={map}
          cities={getLowOrUnreachedCities()}
          isGlobalView={showGlobalRegions}
          showFunding={showFunding}
        />
      )}
      
      {/* Only show city markers if we're in a region or city view, NOT in Mexico country view */}
      {(shouldShowRegionCities || shouldShowSpecialCities) && !(!selectedRegion && isMexicoView) && (
        <CityMarkers
          map={map}
          cities={citiesToShow}
          onCityClick={onCityClick || (() => {})}
          markerRefs={markerRefs.cities}
          selectedCity={selectedCity}
          selectedRegion={selectedRegion}
          showAllMexicoCities={false}
          showFunding={showFunding}
        />
      )}
      
      {(mapZoomLevel === 'city' || showChiapasCities || showHidalgoCities) && selectedCity && (
        <ChurchMarkers 
          map={map}
          regions={selectedRegion ? [selectedRegion] : []}
          filteredRegionIds={filteredRegionIds}
          markerRefs={markerRefs.churches}
        />
      )}
    </>
  );
};

export default CityMarkersLayer;
